/**
 * Web Push (FCM) registration + permission flow.
 *
 * Usage from React:
 *   const { permission, enable, disable } = usePushNotifications();
 *   <button onClick={enable}>Enable weather alerts</button>
 *
 * Env vars (all REACT_APP_ prefix):
 *   REACT_APP_FIREBASE_VAPID_KEY  — Web Push cert public key (Firebase Console
 *     → Project settings → Cloud Messaging → Web Push certificates)
 *   All the standard REACT_APP_FIREBASE_* keys used by /lib/firebase.js
 */
import { useCallback, useEffect, useState } from "react";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { firebaseApp } from "@/lib/firebase";
import { http } from "@/lib/api";

const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY;

function buildSwUrl() {
  // Pass the client Firebase config to the SW as query params so it can init
  // itself without embedding secrets in a static file.
  const params = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

let _messagingPromise = null;
async function getMessagingInstance() {
  if (_messagingPromise) return _messagingPromise;
  _messagingPromise = (async () => {
    if (typeof window === "undefined") return null;
    if (!(await isSupported())) return null;
    return getMessaging(firebaseApp);
  })();
  return _messagingPromise;
}

/**
 * Register the SW and request a FCM token. Returns the token or throws.
 * The caller is responsible for showing UI toasts.
 */
export async function registerForPush() {
  if (!VAPID) throw new Error("Push notifications not configured (missing VAPID key).");
  if (!("serviceWorker" in navigator)) throw new Error("Service workers not supported.");
  if (!("Notification" in window)) throw new Error("Notifications not supported.");

  const messaging = await getMessagingInstance();
  if (!messaging) throw new Error("FCM not supported in this browser.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied.");

  const reg = await navigator.serviceWorker.register(buildSwUrl());
  const token = await getToken(messaging, {
    vapidKey: VAPID,
    serviceWorkerRegistration: reg,
  });
  if (!token) throw new Error("Failed to obtain FCM token.");

  await http.post("/push/register-token", {
    token,
    platform: "web",
    user_agent: navigator.userAgent,
  });

  // Foreground handler — show a native notification even when tab is open.
  onMessage(messaging, (payload) => {
    if (!payload) return;
    const title = payload.notification?.title || "Social Pulse";
    const body = payload.notification?.body || "";
    try {
      if (Notification.permission === "granted") {
        new Notification(title, { body, icon: "/assets/social-pulse-logo.jpg" });
      }
    } catch { /* ignore */ }
  });

  return token;
}

export async function unregisterPush() {
  // Best-effort: get current token then delete on backend.
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return;
    const token = await getToken(messaging, { vapidKey: VAPID }).catch(() => null);
    if (token) {
      await http.post("/push/unregister-token", { token }).catch(() => {});
    }
  } catch { /* ignore */ }
}

/**
 * React hook — surfaces `permission` state and `enable/disable/test` actions.
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default",
  );
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (typeof window === "undefined") return;
        const ok =
          "serviceWorker" in navigator &&
          "Notification" in window &&
          (await isSupported());
        setSupported(!!ok && !!VAPID);
      } catch {
        setSupported(false);
      }
    })();
  }, []);

  const enable = useCallback(async () => {
    await registerForPush();
    setPermission(Notification.permission);
  }, []);

  const disable = useCallback(async () => {
    await unregisterPush();
    setPermission(Notification.permission);
  }, []);

  const sendTest = useCallback(async () => {
    return await http.post("/push/test");
  }, []);

  return { permission, supported, enable, disable, sendTest, configured: !!VAPID };
}
